import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { TemplateCards } from './TemplateCards';
import { CreationSuccessCard } from './CreationSuccessCard';
import { TagReviewCard } from './TagReviewCard';
import { EventReviewCard } from './EventReviewCard';
import { ActivityReviewCard } from './ActivityReviewCard';
import { GoalReviewCard } from './GoalReviewCard';
import { ServiceReviewCard } from './ServiceReviewCard';
import type { ChatSuggestedActivity, ChatSuggestedEvent, ChatSuggestedGoal, ChatSuggestedService } from '@/lib/api/chatCreation.service';

/**
 * Render inline markdown bold (**text**) as <strong> elements.
 * Returns an array of React nodes safe for rendering.
 */
function renderBoldText(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function isJsonContent(text: string): boolean {
  const trimmed = text.trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
    return false;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return parsed !== null && typeof parsed === 'object';
  } catch {
    return false;
  }
}



function getPreviousReviewType(messages: Message[], currentIndex: number): RichMessageData['type'] | undefined {
  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const previous = messages[index];
    if (!previous.isUser && previous.richData?.type) {
      return previous.richData.type;
    }
  }
  return undefined;
}

function getPreviousBotContent(messages: Message[], currentIndex: number): string {
  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const previous = messages[index];
    if (!previous.isUser && previous.content) {
      const normalized = previous.content.toLowerCase();
      if (normalized.includes('tag') || normalized.includes('event') || normalized.includes('activit') || normalized.includes('goal') || normalized.includes('service')) {
        return normalized;
      }
    }
  }
  return '';
}

function inferTypeFromJsonPayload(content: string): 'tags' | 'events' | 'activities' | 'goals' | 'services' | undefined {
  const normalizedContent = content.toLowerCase();
  if (normalizedContent.includes('selectedeventids') || normalizedContent.includes('eventids')) {
    return 'events';
  }
  if (normalizedContent.includes('selectedservicedefinitionids')) {
    return 'services';
  }
  if (normalizedContent.includes('selectedgoaltemplateidsbyevent')) {
    return 'goals';
  }

  try {
    const parsed = JSON.parse(content.trim()) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return undefined;
    }

    if (Array.isArray(parsed)) {
      return undefined;
    }

    const obj = parsed as Record<string, unknown>;

    if (Array.isArray(obj.selectedEventIds) || Array.isArray(obj.eventIds)) {
      return 'events';
    }

    if (obj.selectedGoalTemplateIdsByEvent && typeof obj.selectedGoalTemplateIdsByEvent === 'object') {
      return 'goals';
    }

    const keys = Object.keys(obj);
    if (keys.length === 0) return undefined;

    const allArrayValues = keys.every((key) => Array.isArray(obj[key]));
    if (!allArrayValues) return undefined;

    const looksLikeActivities = keys.some((key) => /[0-9a-f]{8}-[0-9a-f-]{27,}/i.test(key));
    // Only assert 'activities' if confident; otherwise let getPreviousReviewType decide
    return looksLikeActivities ? 'activities' : undefined;
  } catch {
    return undefined;
  }
}

function getJsonSummaryText(messages: Message[], currentIndex: number, isUser: boolean): string {
  if (!isUser) {
    return 'Details captured.';
  }

  const payloadType = inferTypeFromJsonPayload(messages[currentIndex].content);
  if (payloadType === 'tags') return 'Tags updated.';
  if (payloadType === 'events') return 'Events updated.';
  if (payloadType === 'activities') return 'Activities updated.';
  if (payloadType === 'goals') return 'Goals updated.';
  if (payloadType === 'services') return 'Services updated.';

  const reviewType = getPreviousReviewType(messages, currentIndex);
  if (reviewType === 'tag_review') return 'Tags updated.';
  if (reviewType === 'event_review') return 'Events updated.';
  if (reviewType === 'activity_review') return 'Activities updated.';
  if (reviewType === 'goal_review') return 'Goals updated.';
  if (reviewType === 'service_review') return 'Services updated.';

  const previousBotContent = getPreviousBotContent(messages, currentIndex);
  if (previousBotContent.includes('tag')) return 'Tags updated.';
  if (previousBotContent.includes('activit') || previousBotContent.includes('task')) return 'Activities updated.';
  if (previousBotContent.includes('goal')) return 'Goals updated.';
  if (previousBotContent.includes('service')) return 'Services updated.';

  return 'Selections updated.';
}

export interface MessageOption {
  id: string | number;
  label: string;
  value?: string;
  variant?: 'default' | 'primary' | 'auth' | 'chip';
}

export interface RichMessageData {
  type: 'templates' | 'creation_success' | 'tag_review' | 'event_review' | 'activity_review' | 'goal_review' | 'service_review';
  templates?: Array<{
    key: string;
    name: string;
    description: string;
    matchScore: number;
  }>;
  tags?: Record<string, string[]>;
  events?: ChatSuggestedEvent[];
  selectedEvents?: ChatSuggestedEvent[];
  activitySuggestions?: Record<string, ChatSuggestedActivity[]>;
  activityReviewCurrentEventIndex?: number;
  selectedActivities?: Record<string, string[]>;
  goalSuggestions?: Record<string, ChatSuggestedGoal[]>;
  services?: ChatSuggestedService[];
  selectedServiceDefinitionIds?: number[];
  celebrationId?: number;
  celebrationTitle?: string;
  eventsCreated?: number;
  activitiesCreated?: number;
  servicesShortlisted?: number;
  conversationLogId?: number;
}

export interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  options?: MessageOption[];
  richData?: RichMessageData;
}

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  sessionId?: string;
  workflowStatus?: string;
  onSelectOption?: (optionId: string | number, optionValue?: string) => void;
  onOpenCalendar?: () => void;
  onSelectTemplate?: (templateKey: string, templateName: string) => void;
  onSkipTemplate?: () => void;
  onNavigateCelebration?: (celebrationId: number) => void;
  onConfirmTags?: (selectedTags: Record<string, string[]>) => void;
  onSkipTags?: () => void;
  onConfirmEvents?: (selectedEventIds: string[]) => void;
  onSkipEvents?: () => void;
  onConfirmActivities?: (selectedActivities: Record<string, string[]>) => void;
  onPreviousActivities?: () => void;
  onSkipActivities?: () => void;
  onConfirmGoals?: (selectedGoalTemplateIdsByEvent: Record<string, number[]>) => void;
  onSkipGoals?: () => void;
  onConfirmServices?: (selectedServiceDefinitionIds: number[]) => void;
  onSkipServices?: () => void;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  sessionId,
  workflowStatus,
  onSelectOption,
  onOpenCalendar,
  onSelectTemplate,
  onSkipTemplate,
  onNavigateCelebration,
  onConfirmTags,
  onSkipTags,
  onConfirmEvents,
  onSkipEvents,
  onConfirmActivities,
  onPreviousActivities,
  onSkipActivities,
  onConfirmGoals,
  onSkipGoals,
  onConfirmServices,
  onSkipServices,
}) => {
  const bottomRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const lastBotMessageIndex = messages.reduce((last, msg, idx) => !msg.isUser ? idx : last, -1);
  const latestRichMessageIndexByType = React.useMemo<Record<RichMessageData['type'], number>>(() => {
    const indices: Record<RichMessageData['type'], number> = {
      templates: -1,
      creation_success: -1,
      tag_review: -1,
      event_review: -1,
      activity_review: -1,
      goal_review: -1,
      service_review: -1,
    };

    messages.forEach((message, index) => {
      if (!message.isUser && message.richData?.type) {
        indices[message.richData.type] = index;
      }
    });

    return indices;
  }, [messages]);
  const isReadyToCreate = workflowStatus === 'ready_to_create';

  return (
    <div className="flex-1 overflow-y-scroll p-4 space-y-4">
      {messages.map((message, messageIndex) => (
        <div key={message.id}>
          {(() => {
            const hasJsonPayload = isJsonContent(message.content);
            const safeContent = hasJsonPayload
              ? getJsonSummaryText(messages, messageIndex, message.isUser)
              : message.content;

            return safeContent ? (
          <div
            className={cn(
              "flex flex-col",
              message.isUser ? "items-end" : "items-start"
            )}
          >
            <div
              className={cn(
                "max-w-[70%] rounded-lg p-3",
                message.isUser
                  ? "bg-primary/15 text-primary border border-primary/25"
                  : isReadyToCreate && messageIndex === lastBotMessageIndex
                    ? "bg-emerald-50 border border-emerald-200 text-foreground"
                    : "bg-muted"
              )}
            >
              <p className="whitespace-pre-wrap font-medium">{message.isUser ? safeContent : renderBoldText(safeContent)}</p>
            </div>
            <p className="text-xs mt-1 px-1 text-muted-foreground">
              {message.timestamp.toLocaleTimeString()}
            </p>
          </div>
            ) : null;
          })()}
          
          {/* Render rich data cards */}
          {message.richData && !message.isUser && (() => {
            const isLatestRichMessage = latestRichMessageIndexByType[message.richData.type] === messageIndex;
            const canInteractTagReview = isLatestRichMessage && workflowStatus === 'awaiting_tag_review';
            const canInteractEventReview = isLatestRichMessage && workflowStatus === 'awaiting_event_review';
            const canInteractActivityReview = isLatestRichMessage && workflowStatus === 'awaiting_activity_review';
            const canInteractGoalReview = isLatestRichMessage && workflowStatus === 'awaiting_goal_review';
            const canInteractServiceReview = isLatestRichMessage && workflowStatus === 'awaiting_service_review';
            const showCreationSuccess = isLatestRichMessage && workflowStatus === 'action_executed';

            return (
              <div className="flex justify-start mt-3">
                {message.richData.type === 'templates' && message.richData.templates && onSelectTemplate && onSkipTemplate && (
                  <TemplateCards
                    templates={message.richData.templates}
                    onSelect={onSelectTemplate}
                    onSkip={onSkipTemplate}
                  />
                )}
                {message.richData.type === 'tag_review' && message.richData.tags && (
                  <TagReviewCard
                    tags={message.richData.tags}
                    onConfirm={canInteractTagReview ? onConfirmTags : undefined}
                    onSkip={canInteractTagReview ? onSkipTags : undefined}
                    persistenceKey={sessionId}
                  />
                )}
                {message.richData.type === 'event_review' && message.richData.events && (
                  <EventReviewCard
                    events={message.richData.events}
                    activitySuggestions={message.richData.activitySuggestions}
                    goalSuggestions={message.richData.goalSuggestions}
                    onConfirm={canInteractEventReview ? onConfirmEvents : undefined}
                    onSkip={canInteractEventReview ? onSkipEvents : undefined}
                  />
                )}
                {message.richData.type === 'activity_review' && message.richData.selectedEvents && message.richData.activitySuggestions && (
                  <ActivityReviewCard
                    selectedEvents={message.richData.selectedEvents}
                    activitySuggestions={message.richData.activitySuggestions}
                    currentEventIndex={message.richData.activityReviewCurrentEventIndex}
                    selectedActivities={message.richData.selectedActivities}
                    onConfirm={canInteractActivityReview ? onConfirmActivities : undefined}
                    onPrevious={canInteractActivityReview ? onPreviousActivities : undefined}
                    onSkip={canInteractActivityReview ? onSkipActivities : undefined}
                  />
                )}
                {message.richData.type === 'goal_review' && message.richData.selectedEvents && message.richData.goalSuggestions && (
                  <GoalReviewCard
                    selectedEvents={message.richData.selectedEvents}
                    goalSuggestions={message.richData.goalSuggestions}
                    onConfirm={canInteractGoalReview ? onConfirmGoals : undefined}
                    onSkip={canInteractGoalReview ? onSkipGoals : undefined}
                  />
                )}
                {message.richData.type === 'service_review' && message.richData.services && (
                  <ServiceReviewCard
                    services={message.richData.services}
                    selectedServiceDefinitionIds={message.richData.selectedServiceDefinitionIds}
                    selectedEvents={message.richData.selectedEvents}
                    onConfirm={canInteractServiceReview ? onConfirmServices : undefined}
                    onSkip={canInteractServiceReview ? onSkipServices : undefined}
                  />
                )}
                {message.richData.type === 'creation_success' && message.richData.celebrationId && onNavigateCelebration && showCreationSuccess && (
                  <CreationSuccessCard
                    celebrationId={message.richData.celebrationId}
                    title={message.richData.celebrationTitle || 'Your Celebration'}
                    onNavigate={onNavigateCelebration}
                    eventsCreated={message.richData.eventsCreated}
                    activitiesCreated={message.richData.activitiesCreated}
                    servicesShortlisted={message.richData.servicesShortlisted}
                    conversationLogId={message.richData.conversationLogId}
                  />
                )}
              </div>
            );
          })()}

          {/* Render options — chip pills or flat buttons */}
          {message.options && message.options.length > 0 && !message.isUser && (() => {
            const hasChips = message.options.some((o) => o.variant === 'chip');
            // Chips are only interactive on the latest bot message to prevent double-sends.
            const isLatest = messageIndex === lastBotMessageIndex;

            if (hasChips) {
              return (
                <div className="flex justify-start mt-2">
                  <div className="flex flex-wrap gap-2 max-w-[85%]">
                    {message.options.map((option) => {
                      const isCalendar = option.value === '__open_calendar__' || String(option.id).includes('calendar');
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={isLatest ? () => {
                            if (isCalendar && onOpenCalendar) {
                              onOpenCalendar();
                            } else {
                              onSelectOption?.(option.id, option.value);
                            }
                          } : undefined}
                          disabled={!isLatest}
                          className={
                            [
                              'px-3 py-1.5 text-sm rounded-full border font-medium whitespace-nowrap transition-all duration-150',
                              isLatest
                                ? isCalendar
                                  ? 'border-primary/60 bg-primary/10 text-primary font-semibold hover:bg-primary/20 hover:border-primary cursor-pointer shadow-xs'
                                  : 'border-primary/40 bg-background text-foreground hover:bg-primary/10 hover:border-primary/70 cursor-pointer'
                                : 'border-border/40 bg-muted/30 text-muted-foreground opacity-50 cursor-default',
                            ].join(' ')
                          }
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
              <div className="flex justify-start mt-3">
                <div className="space-y-2 w-full max-w-[70%]">
                  {message.options.map((option) => {
                    if (option.variant === 'primary') {
                      return (
                        <Button
                          key={option.id}
                          onClick={() => onSelectOption?.(option.id, option.value)}
                          className="w-full justify-center font-semibold bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                        >
                          {option.label}
                        </Button>
                      );
                    }
                    if (option.variant === 'auth') {
                      return (
                        <Button
                          key={option.id}
                          onClick={() => onSelectOption?.(option.id, option.value)}
                          variant="secondary"
                          className="w-full justify-center font-medium border border-primary/30 hover:border-primary/60"
                        >
                          {option.label}
                        </Button>
                      );
                    }
                    return (
                      <Button
                        key={option.id}
                        onClick={() => onSelectOption?.(option.id, option.value)}
                        variant="outline"
                        className="w-full justify-start text-left"
                      >
                        {option.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      ))}
      
      {isLoading && (
        <div className="flex justify-start">
          <div className="bg-muted rounded-lg p-3">
            <div className="flex space-x-2">
              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-100" />
              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-200" />
            </div>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
};
